import { Button, Col, FormCheck, FormGroup, FormLabel, InputGroup, FormControl, Row } from "react-bootstrap";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import InputText from "../../../components/Forms/Element/InputText";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ImagePreview from "../../../components/Common/ImagePreview";
import Common from "../../../../constants/Common";
import { downloadUploadFile } from "../../../../services/AxiosService";
import "react-datepicker/dist/react-datepicker.css";
import PhoneNumberInput from "../../../components/Common/PhoneNumberInput";
import GenderSelect from "../../../components/Common/GenderSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import { convertArrayBufferToBlob, convertGenderToTranslationId, convertStatusToBoolean } from "../../../../utils/ConverterUtils";
import Api from "../../../../constants/Api";
import CpfInput from "../../../components/Common/CpfInput";
import CnpjInput from "../../../components/Common/CnpjInput";
import { formatCNPJ } from "../../../../utils/FormatUtils";
import AddressRegistration from "../../../components/Custom/Common/Address/AddressRegistration";
import BranchMultiSelect from "../../../components/Custom/Main/Branch/BranchMultiSelect";
import { parseToUTCDate } from "../../../../utils/DateUtils";
import axiosInstance from "../../../../services/AxiosInstance";
import FormSection from "../../../components/Common/Form/FormSection";
import { WEEK_DAY_LONG_LABELS, WEEK_DAYS, buildWorkingHours } from "../../../../utils/WorkDayUtils";
import HintCallout from "../../../components/Common/HintCallout";
import { vacationLastDayLabel, vacationPeriodLabel, vacationReturnLabel, vacationStatus } from "../../../../utils/VacationUtils";
import MultiSelect from "../../../components/Forms/Element/MultiSelect";
import "./EmployeeSchedule.css";

const Form = (props) => {

    const { t } = useTranslation();
    const { status, setStatus, providesService, setProvidesService, servesClubCustomers, setServesClubCustomers, portalAccessEnabled, setPortalAccessEnabled, formik, entityData, setUploadedFile, uploadedFile, isBranchFixed } = props;
    const [showLoading, setShowLoading] = useState(false);
    const [showPortalPassword, setShowPortalPassword] = useState(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [planOptions, setPlanOptions] = useState([]);

    /**
     * Altera um campo da linha de um dia na grade de horário.
     *
     * Substitui o item inteiro em vez de mutar o existente: o formik compara por
     * referência, e alterar o objeto no lugar não redesenharia a linha.
     */
    const changeWorkingHour = (day, field, value) => {
        const updated = (formik.values.workingHours || []).map(item =>
            item.dayOfWeek === day ? { ...item, [field]: value } : item);

        formik.setFieldValue('workingHours', updated);
    };

    /** Liga ou desliga o atendimento no dia, limpando os horários quando vira folga. */
    const toggleWorkDay = (day) => {
        const updated = (formik.values.workingHours || []).map(item => {
            if (item.dayOfWeek !== day) {
                return item;
            }

            const isWorking = !item.isWorking;
            // Folga não guarda horário: deixar o valor antigo ali confunde quem reabre a
            // tela e reapareceria se o dia fosse remarcado sem querer.
            return isWorking
                ? { ...item, isWorking }
                : { ...item, isWorking, startTime: '', endTime: '', breakStartTime: '', breakEndTime: '' };
        });

        formik.setFieldValue('workingHours', updated);
    };

    /**
     * Repete o horário do primeiro dia de atendimento nos demais.
     *
     * A escala costuma ser a mesma a semana inteira, com exceção de um dia ou outro:
     * preencher sete linhas na mão para mudar uma é o tipo de trabalho que o operador
     * abandona no meio.
     */
    const replicateFirstDay = () => {
        const source = (formik.values.workingHours || []).find(item => item.isWorking);
        if (!source) {
            return;
        }

        formik.setFieldValue('workingHours', (formik.values.workingHours || []).map(item =>
            item.isWorking
                ? {
                    ...item,
                    startTime: source.startTime,
                    endTime: source.endTime,
                    breakStartTime: source.breakStartTime,
                    breakEndTime: source.breakEndTime
                }
                : item));
    };

    useEffect(() => {
        // As duas listas são carregadas junto com o formulário para que a permissão fique
        // no cadastro do colaborador, sem duplicar a regra de serviços dentro do plano.
        Promise.all([
            axiosInstance.get(Api.main.product),
            axiosInstance.get(Api.subscription.plan)
        ]).then(([productsResponse, plansResponse]) => {
            const products = Array.isArray(productsResponse.data) ? productsResponse.data : [];
            const plans = plansResponse.data?.content || plansResponse.data || [];
            setServiceOptions(products
                .filter(product => product.type === 'SERVICE')
                .map(product => ({ value: product.id, label: product.name })));
            setPlanOptions(plans.map(plan => ({ value: plan.id, label: plan.name })));
        }).catch(() => {
            // O cadastro continua utilizável mesmo se uma lista opcional falhar ao carregar.
        });
    }, []);

    useEffect(() => {
        // Preenche os valores nos campos.
        if (entityData != null) {
            formik.setFieldValue('firstName', entityData.firstName);
            formik.setFieldValue('lastName', entityData.lastName);
            formik.setFieldValue('phone', entityData.phone);
            formik.setFieldValue('birthDate', entityData.birthDate ? parseToUTCDate(entityData.birthDate) : null);
            // Campo de texto sem valor volta da API como null. Os inputs e o Yup esperam
            // string: colaborador antigo sem CPF fazia a validação quebrar com erro de
            // tipo antes de chegar na mensagem de campo obrigatório.
            formik.setFieldValue('document', entityData.document || '');
            formik.setFieldValue('cnpj', entityData.cnpj ? formatCNPJ(entityData.cnpj) : '');
            formik.setFieldValue('emergencyContactName', entityData.emergencyContactName || '');
            formik.setFieldValue('emergencyContactPhone', entityData.emergencyContactPhone || '');
            formik.setFieldValue('emergencyContactRelationship', entityData.emergencyContactRelationship || '');
            formik.setFieldValue('email', entityData.email || '');
            formik.setFieldValue('pixKey', entityData.pixKey || '');
            formik.setFieldValue('lunchStartTime', entityData.lunchStartTime || '');
            formik.setFieldValue('lunchEndTime', entityData.lunchEndTime || '');
            formik.setFieldValue('vacationEnabled', entityData.vacationEnabled === true);
            formik.setFieldValue('vacationStartDate', entityData.vacationStartDate || '');
            formik.setFieldValue('vacationReturnDate', entityData.vacationReturnDate || '');

            // Colaborador cadastrado antes destes campos vem sem grade e, às vezes, sem
            // nenhum dia, o que na agenda vale como "atende a semana toda". A tela mostra
            // tudo marcado e sem horário para refletir o que já acontece hoje, em vez de
            // sugerir que ele não atende em dia nenhum.
            formik.setFieldValue('workingHours',
                buildWorkingHours(entityData.workingHours, entityData.workDays));

            // Preenche o select
            if (entityData.gender !== null) {
                formik.setFieldValue('gender', {
                    value: entityData.gender,
                    label: t(convertGenderToTranslationId(entityData.gender))
                });
            }

            // Preenche o select
            if (entityData.branches !== null && entityData.branches.length > 0) {
                let branches = [];

                entityData.branches.forEach(branch => {
                    branches.push(
                        { value: branch.id, label: branch.name }
                    );
                });

                formik.setFieldValue('branches', branches);
            }

            if (entityData.address !== null) {
                formik.setFieldValue('street', entityData.address.street);
                formik.setFieldValue('number', entityData.address.number);
                formik.setFieldValue('complement', entityData.address.complement);
                formik.setFieldValue('neighborhood', entityData.address.neighborhood);
                formik.setFieldValue('city', entityData.address.city);
                formik.setFieldValue('state', entityData.address.state);
                formik.setFieldValue('zipCode', entityData.address.zipCode);
            }

            // Se possuir imagem cadastrada, exibe-a na tela.
            if (entityData.uploadedFileLocation !== null) {
                setShowLoading(true)
                downloadUploadFile(Api.main.employee + "/download", entityData.id)
                    .then(resp => {
                        if (resp.data) {
                            setUploadedFile(convertArrayBufferToBlob(resp.data));
                        }
                    })
                    .catch(() => {
                        // Arquivo não encontrado no S3 (ex.: registro antigo) - não trava o usuário com erro.
                    })
                    .finally(() => setShowLoading(false));
            }

            setStatus(convertStatusToBoolean(entityData.status));

            // Ausente significa "atende": cadastro antigo, feito antes do campo existir, não
            // pode abrir a tela como bloqueado.
            setServesClubCustomers(entityData.servesClubCustomers !== false);
            if (entityData.providesService !== undefined && entityData.providesService !== null) {
                setProvidesService(entityData.providesService);
            }

            formik.setFieldValue('serviceIds', entityData.serviceIds || []);
            formik.setFieldValue('planIds', entityData.planIds || []);

            if (setPortalAccessEnabled) {
                setPortalAccessEnabled(entityData.portalAccessEnabled === true);
            }
        }

        // eslint-disable-next-line
    }, [entityData]);

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('basic_information')}
                    description={t('employee_basic_information_hint')}
                    icon="fa-solid fa-user-tie"
                >
                    <Row className="g-3 align-items-start">
                        <Col lg={2} md={3}>
                            <div className="avatar-upload-wrap">
                                <ImagePreview
                                    id="file"
                                    permittedWidth={Common.PROFILE_IMAGE_PERMITTED_WIDTH}
                                    permittedHeight={Common.PROFILE_IMAGE_PERMITTED_HEIGHT}
                                    permittedSize={Common.PROFILE_IMAGE_PERMITTED_FILE_SIZE}
                                    setUploadedFile={setUploadedFile}
                                    uploadedFile={uploadedFile}
                                    showLoading={showLoading}
                                />
                                <span className="avatar-upload-label">{t('picture')}</span>
                            </div>
                        </Col>
                        <Col lg={10} md={9}>
                            <Row className="g-3">
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('firstName')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="firstName"
                                            placeholder={t('firstName_placeholder')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.firstName}
                                            error={formik.errors.firstName}
                                            maxlenght="30"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('lastName')} <AsteriskRequired /></FormLabel>
                                        <InputText
                                            name="lastName"
                                            placeholder={t('lastName_placeholder')}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.lastName}
                                            error={formik.errors.lastName}
                                            maxlenght="30"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('phone')} <AsteriskRequired /></FormLabel>
                                        <PhoneNumberInput
                                            name="phone"
                                            formik={formik}
                                            error={formik.errors.phone}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('cpf')} <AsteriskRequired /></FormLabel>
                                        <CpfInput
                                            name="document"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.document}
                                            error={formik.errors.document}
                                            placeholder={t('document')}
                                            maxlenght="30"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={6}>
                                    <FormGroup>
                                        <FormLabel>{t('email')}</FormLabel>
                                        <InputText
                                            name="email"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.email}
                                            error={formik.errors.email}
                                            placeholder={t('email_placeholder')}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('gender')}</FormLabel>
                                        <GenderSelect
                                            name="gender"
                                            value={formik.values.gender}
                                            onChange={(value) =>
                                                formik.setFieldValue('gender', value)
                                            }
                                            onBlur={formik.handleBlur}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('birthDate')}</FormLabel>
                                        <InputDate
                                            name="birthDate"
                                            selected={formik.values.birthDate}
                                            onChange={(value) =>
                                                formik.setFieldValue('birthDate', value)
                                            }
                                            onBlur={formik.handleBlur}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('cnpj')}</FormLabel>
                                        <CnpjInput
                                            name="cnpj"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.cnpj}
                                            error={formik.errors.cnpj}
                                            placeholder={t('document')}
                                            maxlenght="18"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6} xl={3}>
                                    <FormGroup>
                                        <FormLabel>{t('pix_key')}</FormLabel>
                                        <InputText
                                            name="pixKey"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.pixKey}
                                            error={formik.errors.pixKey}
                                            placeholder={t('pix_key_placeholder')}
                                            maxlenght="255"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title={t('work_location')}
                    description={t('employee_work_location_hint')}
                    icon="fa-solid fa-building"
                >
                    <Row className="g-3">
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('branches')} <AsteriskRequired /></FormLabel>
                                <BranchMultiSelect
                                    name="branches"
                                    value={formik.values.branches}
                                    onChange={(value) =>
                                        formik.setFieldValue('branches', value)
                                    }
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.branches}
                                    isDisabled={isBranchFixed} />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title={t('address.label')}
                    description={t('employee_address_hint')}
                    icon="fa-solid fa-location-dot"
                >
                    <AddressRegistration formik={formik} />
                </FormSection>

                <FormSection
                    title={t('emergency_contact')}
                    description={t('employee_emergency_contact_hint')}
                    icon="fa-solid fa-phone-volume"
                >
                    <Row className="g-3">
                        <Col md={6} xl={4}>
                            <FormGroup>
                                <FormLabel>{t('emergency_contact_name')}</FormLabel>
                                <InputText
                                    name="emergencyContactName"
                                    placeholder={t('emergency_contact_name_placeholder')}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.emergencyContactName}
                                    error={formik.errors.emergencyContactName}
                                    maxlenght="50"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} xl={4}>
                            <FormGroup>
                                <FormLabel>{t('emergency_contact_phone')}</FormLabel>
                                <PhoneNumberInput
                                    name="emergencyContactPhone"
                                    formik={formik}
                                    error={formik.errors.emergencyContactPhone}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} xl={4}>
                            <FormGroup>
                                <FormLabel>{t('emergency_contact_relationship')}</FormLabel>
                                <InputText
                                    name="emergencyContactRelationship"
                                    placeholder={t('emergency_contact_relationship_placeholder')}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.emergencyContactRelationship}
                                    error={formik.errors.emergencyContactRelationship}
                                    maxlenght="30"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title="Portal do colaborador"
                    description="Permite ao colaborador acessar a própria agenda e previsão de comissões."
                    icon="fa-solid fa-user-shield"
                    compact
                >
                    <Row>
                        <Col md={12}>
                            <FormGroup className="mb-0">
                                {/* Colaborador inativo não tem portal: o servidor desliga o acesso ao
                                    gravar, então a caixa fica travada em vez de prometer o que não vale. */}
                                <StatusCheckBox
                                    name="portalAccessEnabled"
                                    label="Habilitar acesso ao portal do colaborador"
                                    defaultChecked={portalAccessEnabled}
                                    checked={portalAccessEnabled}
                                    disabled={!status}
                                    onClick={() => setPortalAccessEnabled && setPortalAccessEnabled(!portalAccessEnabled)}
                                />
                                {!status && (
                                    <small className="text-muted d-block mt-2">
                                        Colaborador inativo não acessa o portal. Ative o colaborador na
                                        seção Situação para habilitar o acesso.
                                    </small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    {portalAccessEnabled && (
                        <Row className="g-3 mt-1">
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>
                                        {entityData?.portalAccessEnabled ? 'Nova senha (deixe em branco para manter)' : 'Senha de acesso'}
                                        {!entityData?.portalAccessEnabled && <span className="text-danger ms-1">*</span>}
                                    </FormLabel>
                                    <InputGroup>
                                        <FormControl
                                            type={showPortalPassword ? 'text' : 'password'}
                                            name="portalPassword"
                                            value={formik.values.portalPassword || ''}
                                            onChange={formik.handleChange}
                                            placeholder="••••••••"
                                            autoComplete="new-password"
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setShowPortalPassword(v => !v)}
                                            tabIndex={-1}
                                        >
                                            <i className={`fa-solid ${showPortalPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </Button>
                                    </InputGroup>
                                    <small className="text-muted">O colaborador usará o e-mail cadastrado + esta senha para acessar o portal.</small>
                                </FormGroup>
                            </Col>

                        </Row>
                    )}
                </FormSection>

                <FormSection
                    title="Agendamento"
                    description="Define se o colaborador realiza serviços, quem ele atende e em quais dias."
                    icon="fa-solid fa-calendar-check"
                    compact
                >
                    <Row className="employee-appointment__layout">
                        <Col md={12}>
                            <div className="employee-appointment__block">
                                <div className="employee-appointment__block-heading">
                                    <h3>Disponibilidade para atendimento</h3>
                                    <p>Defina se o colaborador aparece na agenda e se pode atender clientes do clube.</p>
                                </div>
                                <StatusCheckBox
                                    name="providesService"
                                    label="Presta serviço?"
                                    defaultChecked={providesService}
                                    checked={providesService}
                                    onClick={() => setProvidesService(!providesService)} />
                                <small className="employee-appointment__helper">Quando desmarcado, o colaborador não fica disponível para agendamentos.</small>
                            </div>
                        </Col>
                        {/* Só faz sentido para quem atende: quem não presta serviço não vai
                            atender assinante nenhum, e o campo aparecendo ali levanta a
                            pergunta de por que existem duas marcas parecidas. */}
                        {providesService && (
                            <Col md={12}>
                                <div className="employee-appointment__block">
                                    <StatusCheckBox
                                        name="servesClubCustomers"
                                        label="Atende clientes do clube?"
                                        defaultChecked={servesClubCustomers}
                                        checked={servesClubCustomers}
                                        onClick={() => setServesClubCustomers(!servesClubCustomers)} />
                                    <small className="employee-appointment__helper">Desmarcado, ele não atende assinantes, mesmo em serviços avulsos.</small>
                                </div>
                            </Col>
                        )}
                        {/* Férias no modelo da resposta automática do Gmail: liga, informa a
                            saída e a volta, e a agenda fecha sozinha nesse período. */}
                        {providesService && (
                            <Col md={12}>
                                <div className={`employee-appointment__block employee-vacation${formik.values.vacationEnabled ? ' is-enabled' : ''}`}>
                                    <div className="employee-vacation__header">
                                        <div className="employee-appointment__block-heading mb-0">
                                            <h3><i className="fa-solid fa-umbrella-beach me-2" aria-hidden="true"></i>Férias</h3>
                                            <p>Com as férias ligadas, a agenda fica bloqueada no período e mostra o motivo.</p>
                                        </div>
                                        <FormCheck
                                            type="switch"
                                            id="employee-vacation-enabled"
                                            className="employee-vacation__switch"
                                            label={formik.values.vacationEnabled ? 'Ligadas' : 'Desligadas'}
                                            checked={Boolean(formik.values.vacationEnabled)}
                                            onChange={() => formik.setFieldValue('vacationEnabled', !formik.values.vacationEnabled)}
                                        />
                                    </div>

                                    {formik.values.vacationEnabled && (
                                        <>
                                            <Row className="g-3 mt-1">
                                                <Col xs={12} sm={6} md={4}>
                                                    <FormGroup className="mb-0">
                                                        <FormLabel htmlFor="employee-vacation-start">Data de saída <AsteriskRequired /></FormLabel>
                                                        <FormControl
                                                            type="date"
                                                            id="employee-vacation-start"
                                                            name="vacationStartDate"
                                                            value={formik.values.vacationStartDate || ''}
                                                            onChange={formik.handleChange}
                                                        />
                                                        <small className="employee-appointment__helper">Primeiro dia fora.</small>
                                                        {formik.errors.vacationStartDate && (
                                                            <small className="text-danger d-block">{formik.errors.vacationStartDate}</small>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                                <Col xs={12} sm={6} md={4}>
                                                    <FormGroup className="mb-0">
                                                        <FormLabel htmlFor="employee-vacation-return">Data de volta <AsteriskRequired /></FormLabel>
                                                        <FormControl
                                                            type="date"
                                                            id="employee-vacation-return"
                                                            name="vacationReturnDate"
                                                            min={formik.values.vacationStartDate || undefined}
                                                            value={formik.values.vacationReturnDate || ''}
                                                            onChange={formik.handleChange}
                                                        />
                                                        <small className="employee-appointment__helper">Dia em que volta a atender.</small>
                                                        {formik.errors.vacationReturnDate && (
                                                            <small className="text-danger d-block">{formik.errors.vacationReturnDate}</small>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            {(() => {
                                                // Resumo do que vai acontecer na agenda, calculado com as
                                                // mesmas regras que a agenda usa ao exibir o bloqueio.
                                                const preview = {
                                                    vacationEnabled: true,
                                                    vacationStartDate: formik.values.vacationStartDate,
                                                    vacationReturnDate: formik.values.vacationReturnDate
                                                };
                                                const status = vacationStatus(preview);
                                                if (!status) return null;

                                                const summary = {
                                                    scheduled: `A agenda será bloqueada de ${vacationPeriodLabel(preview)}. Volta a atender em ${vacationReturnLabel(preview)}.`,
                                                    active: `De férias agora. A agenda está bloqueada até ${vacationLastDayLabel(preview)} e volta a abrir em ${vacationReturnLabel(preview)}.`,
                                                    finished: `Este período já terminou (${vacationPeriodLabel(preview)}). A agenda está aberta normalmente; desligue as férias ou informe um novo período.`
                                                }[status];

                                                return (
                                                    <div className={`employee-vacation__summary is-${status}`}>
                                                        <i className={`fa-solid ${status === 'finished' ? 'fa-circle-info' : 'fa-lock'}`} aria-hidden="true"></i>
                                                        <span>{summary}</span>
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>
                            </Col>
                        )}
                        {providesService && (
                            <>
                                <Col md={12}>
                                    <div className="employee-appointment__block">
                                        <div className="employee-appointment__block-heading">
                                            <h3>Restrições de atendimento</h3>
                                            <p>Opcional. Sem seleção, o colaborador continua atendendo todos os serviços e planos.</p>
                                        </div>
                                        <Row className="g-3">
                                <Col md={6}>
                                    <FormGroup className="mb-0">
                                        <FormLabel>Serviços realizados</FormLabel>
                                        <MultiSelect
                                            options={serviceOptions}
                                            value={serviceOptions.filter(option =>
                                                (formik.values.serviceIds || []).includes(option.value))}
                                            onChange={value => formik.setFieldValue(
                                                'serviceIds', (value || []).map(option => option.value))}
                                            placeholder="Sem restrição de serviço"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup className="mb-0">
                                        <FormLabel>Planos atendidos</FormLabel>
                                        <MultiSelect
                                            options={planOptions}
                                            value={planOptions.filter(option =>
                                                (formik.values.planIds || []).includes(option.value))}
                                            onChange={value => formik.setFieldValue(
                                                'planIds', (value || []).map(option => option.value))}
                                            placeholder="Sem restrição de plano"
                                        />
                                    </FormGroup>
                                </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </>
                        )}
                        <Col md={12} className="employee-appointment__schedule-col">
                            <FormGroup className="mb-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                    <div className="employee-appointment__block-heading mb-0">
                                        <h3>Dias e horários de atendimento</h3>
                                        <p>Marque os dias e informe horários apenas quando quiser restringir o funcionamento da filial.</p>
                                    </div>
                                </div>

                                <HintCallout>
                                    Deixe a entrada e a saída <strong>em branco</strong> para o colaborador seguir o
                                    horário da filial no dia. O horário informado aqui só <strong>restringe</strong>:
                                    se a filial abre às 9h, começar 8h não adianta.
                                </HintCallout>

                                <div className="d-flex justify-content-end mt-2 mb-3">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        onClick={replicateFirstDay}
                                    >
                                        <i className="fa-solid fa-copy me-2"></i>
                                        Repetir nos demais dias
                                    </Button>
                                </div>

                                {/* O dia fica no topo da própria linha e identifica tanto a jornada
                                    quanto a pausa configuradas logo abaixo. */}
                                <div className="employee-schedule">
                                    {WEEK_DAYS.map(day => {
                                        const hours = (formik.values.workingHours || [])
                                            .find(item => item.dayOfWeek === day.value) || {};

                                        return (
                                            <div
                                                key={day.value}
                                                className={`employee-schedule__row${hours.isWorking ? '' : ' is-off'}`}
                                            >
                                                <div className="employee-schedule__day">
                                                    <FormCheck
                                                        type="switch"
                                                        id={`work-day-${day.value}`}
                                                        checked={Boolean(hours.isWorking)}
                                                        onChange={() => toggleWorkDay(day.value)}
                                                        label={WEEK_DAY_LONG_LABELS[day.value]}
                                                    />
                                                </div>

                                                {hours.isWorking ? (
                                                    <div className="employee-schedule__times">
                                                        <div className="employee-schedule__group employee-schedule__group--work">
                                                            <span className="employee-schedule__group-title">Atendimento</span>
                                                            <label>
                                                                <span>Entrada</span>
                                                                <FormControl
                                                                    type="time"
                                                                    value={hours.startTime || ''}
                                                                    onChange={(e) => changeWorkingHour(day.value, 'startTime', e.target.value)}
                                                                />
                                                            </label>
                                                            <label>
                                                                <span>Saída</span>
                                                                <FormControl
                                                                    type="time"
                                                                    value={hours.endTime || ''}
                                                                    onChange={(e) => changeWorkingHour(day.value, 'endTime', e.target.value)}
                                                                />
                                                            </label>
                                                        </div>
                                                        <div className="employee-schedule__group employee-schedule__group--break">
                                                            <span className="employee-schedule__group-title">Intervalo do dia</span>
                                                            <label>
                                                                <span>Início</span>
                                                                <FormControl
                                                                    type="time"
                                                                    value={hours.breakStartTime || ''}
                                                                    onChange={(e) => changeWorkingHour(day.value, 'breakStartTime', e.target.value)}
                                                                />
                                                            </label>
                                                            <label>
                                                                <span>Fim</span>
                                                                <FormControl
                                                                    type="time"
                                                                    value={hours.breakEndTime || ''}
                                                                    onChange={(e) => changeWorkingHour(day.value, 'breakEndTime', e.target.value)}
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="employee-schedule__off">Folga</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {formik.errors.workingHours && (
                                    <small className="text-danger d-block mt-1">{formik.errors.workingHours}</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <div className="employee-schedule__default-break">
                                <div className="employee-schedule__default-break-heading">
                                    <h6>Intervalo padrão do colaborador</h6>
                                    <p>
                                        Quando um dia não tiver um <strong>Intervalo do dia</strong> definido, será usado
                                        o horário configurado aqui. A agenda bloqueará agendamentos durante essa pausa.
                                        Se os campos ficarem em branco, será usado o intervalo da filial, quando configurado.
                                    </p>
                                </div>
                                <Row>
                                    <Col sm={6} md={4}>
                                        <FormGroup className="mb-0">
                                            <FormLabel htmlFor="employee-lunch-start">Início</FormLabel>
                                            <FormControl
                                                type="time"
                                                id="employee-lunch-start"
                                                name="lunchStartTime"
                                                value={formik.values.lunchStartTime || ''}
                                                onChange={formik.handleChange}
                                            />
                                            {formik.errors.lunchStartTime && (
                                                <small className="text-danger">{formik.errors.lunchStartTime}</small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col sm={6} md={4}>
                                        <FormGroup className="mb-0">
                                            <FormLabel htmlFor="employee-lunch-end">Fim</FormLabel>
                                            <FormControl
                                                type="time"
                                                id="employee-lunch-end"
                                                name="lunchEndTime"
                                                value={formik.values.lunchEndTime || ''}
                                                onChange={formik.handleChange}
                                            />
                                            {formik.errors.lunchEndTime && (
                                                <small className="text-danger">{formik.errors.lunchEndTime}</small>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    title={t('status')}
                    description={t('employee_status_hint')}
                    icon="fa-solid fa-toggle-on"
                    compact
                >
                    <Row>
                        <Col md={12}>
                            <FormGroup className="mb-0">
                                {/* Inativar desliga o portal junto, do mesmo jeito que o servidor faz
                                    ao gravar: a tela mostra na hora o que vai ser salvo. A senha é
                                    preservada e volta a valer se a pessoa for reativada. */}
                                <StatusCheckBox
                                    name="status"
                                    defaultChecked={status}
                                    checked={status}
                                    onClick={() => {
                                        const next = !status;
                                        setStatus(next);
                                        if (!next && setPortalAccessEnabled) {
                                            setPortalAccessEnabled(false);
                                        }
                                    }} />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
